<template>
    <InsightCardLayout>
        <template v-if="showRating" #before>
            <MyWorkInsightFeedback
                :feedback-value="feedbackValue"
                :feedback-tags="feedbackTags"
                :feedback-comment="feedbackComment"
                :disabled="feedbackFetching"
                :feedback-submitting="feedbackSubmitting"
                :submit-error="!!submitError"
                :reason-prompt="reasonPrompt"
                @select="handleFeedbackSelect"
                @submit-feedback="handleSubmitFeedback" />
        </template>
        <!-- Category + date row -->
        <div class="flex items-center gap-2 mb-3">
            <InsightCategoryLabel v-if="experimentTitle" :label="experimentTitle" :experiment-slug="experimentSlug" />
            <span v-if="createdAt" class="scaffold-details-small text-insight-eyebrow-text">• <BaseDate :value="createdAt" format="month-day" /></span>
        </div>

        <!-- Title -->
        <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
        <h3 class="display-small-serif text-ink mb-3">
            <MarkdownText :text="title" inline />
        </h3>

        <!-- Divider -->
        <div class="border-t border-border mb-2.5" role="separator"></div>

        <!-- Summary with inline "See more" -->
        <div
            class="summary-with-toggle body-small prose prose-sm max-w-none text-ink leading-relaxed prose-headings:my-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-5">
            <MarkdownText :text="summary" />
            <BaseButton
                v-if="hasExpandableContent && showSeeMore"
                type="button"
                theme="tertiary"
                size="sm"
                :aria-expanded="isExpanded"
                :aria-controls="`insight-detail-${insightId}`"
                @click="isExpanded = !isExpanded">
                {{ isExpanded ? "See less" : "See more" }}
            </BaseButton>
        </div>

        <!-- Expandable detail box -->
        <div v-if="isExpanded" :id="`insight-detail-${insightId}`" class="rounded-2xl border border-border bg-surface-sunken px-4 py-2.5 my-2.5 flex flex-col gap-3">
            <div v-if="content" class="detail-with-label prose prose-sm max-w-none text-ink leading-relaxed prose-p:my-1 prose-ul:my-1 prose-ol:my-1">
                <span class="detail-label font-bold">Why I'm flagging this: </span>
                <MarkdownText :text="content" />
            </div>
        </div>

        <!-- Unknowns -->
        <div v-if="unknowns.length" class="flex flex-col gap-2 mt-2.5">
            <span class="body-small text-ink">What I'd love to understand:</span>
            <ul class="list-disc pl-5">
                <li v-for="unknown in unknowns" :key="unknown" class="body-small text-ink leading-relaxed">
                    {{ unknown }}
                </li>
            </ul>
        </div>

        <!-- Footer: divider + Based on chips + Dive Deeper -->
        <div class="border-t border-border mt-3 pt-3 flex items-center justify-between gap-4">
            <InsightEvidenceChips :evidence-groups="evidenceGroups" :analytics-context="{ insightId, experimentSlug, location }" />
            <DiveDeeperButton v-if="interactive" class="ml-auto" :loading="isStartingChat" @click="handleDiveDeeper" />
        </div>
        <span v-if="chatError" role="alert" class="details text-chip-status-overdue-text mt-1">{{ chatError }}</span>
    </InsightCardLayout>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import { type LabInsightLocation, useLabInsightActions } from "~vue/composables/useLabInsightActions";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import { evidenceSourceCounts, type InsightEvidenceGroup } from "~vue/Labs/evidence";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import MyWorkInsightFeedback, { type ReasonPrompt } from "~vue/Labs/MyWorkInsightFeedback.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

interface Props {
    experimentTitle: string;
    experimentSlug: string;
    title: string;
    summary: string;
    content?: string;
    insightId: string;
    unknowns?: string[];
    showSeeMore?: boolean;
    createdAt?: string | null;
    evidenceGroups?: InsightEvidenceGroup[];
    /** Where the card is rendered, for analytics and evidence-chip context. */
    location?: LabInsightLocation;
    /** Offer "Dive Deeper". Off during onboarding, where leaving setup for chat would lose the flow. */
    interactive?: boolean;
    /** Offer the rating bar. Independent of `interactive`: each surface decides the two separately. */
    showRating?: boolean;
    /** Passed to the rating bar; onboarding asks for a reason only on a negative. */
    reasonPrompt?: ReasonPrompt;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    unknowns: () => [],
    showSeeMore: false,
    createdAt: null,
    location: "mywork",
    interactive: true,
    showRating: true,
    reasonPrompt: "always",
});

const isExpanded = ref(false);
const hasExpandableContent = computed(() => Boolean(props.content));

const cardEventData = () => ({
    insight_id: props.insightId,
    experiment_title: props.experimentTitle,
    experiment_slug: props.experimentSlug,
    location: props.location,
    evidence_source_counts: evidenceSourceCounts(props.evidenceGroups ?? []),
});

const {
    feedbackValue,
    feedbackTags,
    feedbackComment,
    feedbackFetching,
    feedbackSubmitting,
    submitError,
    handleFeedbackSelect: submitFeedbackSelect,
    handleSubmitFeedback,
    isStartingChat,
    chatError,
    handleStartChat,
} = useLabInsightActions(props.insightId, props.location, { fetchExistingFeedback: props.showRating });

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, cardEventData());
});

function handleDiveDeeper() {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED, cardEventData());
    handleStartChat();
}

async function handleFeedbackSelect(value: 0 | 1) {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_FEEDBACK_SUBMITTED, { ...cardEventData(), value });
    await submitFeedbackSelect(value);
}
</script>

<style scoped>
/* Make the last paragraph from MarkdownText render inline so the
   "See more" button flows on the same line as the summary text.
   Chain: wrapper → MarkdownText root (inline) → [data-marked] div (inline) → last <p> (inline) → button (inline) */
.summary-with-toggle :deep([data-marked]) {
    display: inline;
}

.summary-with-toggle :deep([data-marked] > p:last-of-type) {
    display: inline;
}

/* Make the bold label flow inline with the first paragraph of the detail content. */
.detail-with-label .detail-label {
    display: inline;
}

.detail-with-label :deep([data-marked]) {
    display: inline;
}

.detail-with-label :deep([data-marked] > p:first-of-type) {
    display: inline;
}
</style>
